// Gmail Drafts integration.
//
// What this does:
//   When the owner clicks "Approve & invoice" on a job, we generate the
//   per-guard workbook(s) + POD PDF in memory, then POST a fully-formed
//   MIME message (subject, To:, body, attachments) to the Gmail API
//   users.drafts.create endpoint. The result is a draft sitting in the
//   owner's Gmail Drafts folder, ready to review and Send with one click.
//
// What this DOES NOT do:
//   We never call users.messages.send. The OAuth scope we request is
//   gmail.compose — drafts-only. The email cannot leave the owner's
//   inbox without their explicit "Send" click in Gmail. This is both
//   safer (least-privilege) and gives the owner one more chance to
//   review before the invoice goes to the broker.
//
// Auth model:
//   We use Google Identity Services (GIS) token client in popup mode
//   (loaded in index.html). The first time the owner clicks Connect
//   Gmail or Approve, a Google consent popup appears; subsequent calls
//   reuse the cached access token (which expires in ~1 hour — at which
//   point we silently re-prompt). No backend, no refresh token storage.
//   Tradeoff: ~once-per-session re-auth vs. complexity of storing a
//   refresh token. Acceptable for v1 — revisit if it gets annoying.

import { getCompany, updateCompany } from "./firestore";

// Client ID is sourced from a build-time env var so different deploys
// (dev / prod) can point at different Google projects. Set
// REACT_APP_GOOGLE_CLIENT_ID in your .env (or .env.production) to the
// "Web application" OAuth client ID from Google Cloud Console.
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Drafts-only. Does NOT grant gmail.send. Important — if you ever need
// the integration to also send emails directly (skipping the human-in-
// the-loop step), update this here AND in the OAuth consent screen,
// then re-prompt every owner so the new scope is granted.
const SCOPES = "https://www.googleapis.com/auth/gmail.compose";

// ── Token cache ──────────────────────────────────────────────────────
// Access tokens live in memory only. They expire (typically 1 hour),
// and we re-prompt with a popup when that happens. Persisting them
// across reloads would require either localStorage (XSS exposure) or a
// backend with the refresh-token grant — both are overkill for v1.
let cachedAccessToken = null;
let cachedTokenExpiresAt = 0;
// Email of the Google account whose access token we hold. Needed so the
// "Open draft" deep link can include ?authuser={email} — without it,
// Gmail opens whichever account is sitting at /u/0/ in the user's
// browser, which often isn't the one they Connected.
let cachedUserEmail = null;

function tokenIsFresh() {
  // 60s safety margin so we don't ship a request that lands milliseconds
  // after the token actually expires server-side.
  return cachedAccessToken && Date.now() + 60_000 < cachedTokenExpiresAt;
}

/**
 * Hit gmail.users.getProfile to figure out which Google account this
 * access token belongs to. Cached for the session — the email won't
 * change for a given token, and getProfile is cheap but not free.
 * Returns null on failure so callers can fall back to /u/0/.
 */
async function fetchUserEmail(accessToken) {
  if (cachedUserEmail) return cachedUserEmail;
  try {
    const resp = await fetch("https://gmail.googleapis.com/gmail/v1/users/me/profile", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    cachedUserEmail = data.emailAddress || null;
    return cachedUserEmail;
  } catch {
    return null;
  }
}

/** Email of the currently-connected account, if known. */
export function getConnectedEmail() {
  return cachedUserEmail;
}

/**
 * Returns the GIS token client instance. We create it lazily — index.html
 * loads the GSI script async, so on first call it may not be on window
 * yet; this function waits up to 5 seconds for it.
 */
async function getTokenClient(prompt) {
  if (!CLIENT_ID) {
    throw new Error(
      "Gmail integration is not configured: REACT_APP_GOOGLE_CLIENT_ID is empty. " +
      "Set it in .env.production and rebuild."
    );
  }
  // Wait for GIS to load (index.html includes it async).
  const start = Date.now();
  while (!window.google?.accounts?.oauth2) {
    if (Date.now() - start > 5_000) {
      throw new Error("Google Identity Services didn't load. Check your network / blockers.");
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 100));
  }
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      // "" = silent re-auth (no popup if user already consented this session);
      // "consent" = always show the popup (used on first connect).
      prompt: prompt || "",
      callback: (resp) => {
        if (resp?.error) return reject(new Error(`OAuth: ${resp.error}`));
        if (!resp?.access_token) return reject(new Error("No access_token returned."));
        cachedAccessToken = resp.access_token;
        // expires_in is in seconds; default to 1h if missing.
        cachedTokenExpiresAt = Date.now() + (Number(resp.expires_in) || 3600) * 1000;
        resolve(cachedAccessToken);
      },
      error_callback: (err) => reject(new Error(err?.message || "OAuth popup cancelled")),
    });
    client.requestAccessToken();
  });
}

/**
 * Get a Gmail-scoped access token. Returns the cached value if still
 * fresh, otherwise pops up a Google sign-in / re-consent window.
 *
 * Options:
 *   interactive — when false, a missing token throws instead of
 *     prompting. Use this for background flushes that shouldn't
 *     surprise the user with a popup. Defaults to true.
 *   forceConsent — show the consent screen even if we already have a
 *     token. Used by "Connect Gmail" in Settings to let the owner
 *     re-authorize / switch accounts.
 */
export async function getAccessToken({ interactive = true, forceConsent = false } = {}) {
  if (!forceConsent && tokenIsFresh()) return cachedAccessToken;
  if (!interactive) throw new Error("Gmail not connected this session — open Settings to connect.");
  return getTokenClient(forceConsent ? "consent" : "");
}

export function isGmailConnected() {
  return tokenIsFresh();
}

export function disconnectGmail() {
  cachedAccessToken = null;
  cachedTokenExpiresAt = 0;
  cachedUserEmail = null;
}

// ── MIME builder ─────────────────────────────────────────────────────
// Gmail's drafts.create accepts a base64url-encoded RFC822 message.
// We build it as multipart/mixed with one HTML body part + N file parts.

function b64urlEncode(input) {
  // btoa requires a binary string. For Unicode safety, encode via
  // TextEncoder → spread bytes. Replace +/= per RFC 4648 base64url.
  const bytes = typeof input === "string"
    ? new TextEncoder().encode(input)
    : new Uint8Array(input);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function blobToBase64(blob) {
  const buf = await blob.arrayBuffer();
  // Standard base64 (not url-safe) — Gmail wants attachments base64 not
  // base64url.
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * Build a base64url-encoded MIME multipart/mixed message.
 *
 *   to/cc      — string or array of strings
 *   subject    — string
 *   bodyHtml   — string (text part)
 *   attachments — array of { filename, mimeType, blob }
 */
async function buildMimeMessage({ to, cc, subject, bodyHtml, attachments = [] }) {
  const boundary = `===veloceflow-${Date.now()}-${Math.random().toString(36).slice(2)}===`;
  const toLine = Array.isArray(to) ? to.join(", ") : (to || "");
  const ccLine = cc ? (Array.isArray(cc) ? cc.join(", ") : cc) : "";

  let raw = "";
  raw += `To: ${toLine}\r\n`;
  if (ccLine) raw += `Cc: ${ccLine}\r\n`;
  raw += `Subject: ${subject}\r\n`;
  raw += `MIME-Version: 1.0\r\n`;
  raw += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n\r\n`;

  // ── Body part (HTML) ──
  raw += `--${boundary}\r\n`;
  raw += `Content-Type: text/html; charset="UTF-8"\r\n`;
  raw += `Content-Transfer-Encoding: 7bit\r\n\r\n`;
  raw += `${bodyHtml}\r\n\r\n`;

  // ── Attachment parts ──
  for (const att of attachments) {
    if (!att?.blob) continue;
    // eslint-disable-next-line no-await-in-loop
    const b64 = await blobToBase64(att.blob);
    raw += `--${boundary}\r\n`;
    raw += `Content-Type: ${att.mimeType || "application/octet-stream"}; name="${att.filename}"\r\n`;
    raw += `Content-Transfer-Encoding: base64\r\n`;
    raw += `Content-Disposition: attachment; filename="${att.filename}"\r\n\r\n`;
    // Wrap base64 at 76 chars per RFC 2045 — some MIME-strict mail
    // servers reject unwrapped lines.
    const wrapped = b64.replace(/(.{76})/g, "$1\r\n");
    raw += `${wrapped}\r\n\r\n`;
  }

  raw += `--${boundary}--\r\n`;
  return b64urlEncode(raw);
}

// ── Templating ───────────────────────────────────────────────────────
function fillTemplate(template, ctx) {
  if (!template) return "";
  return String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const v = ctx[key];
    return v == null ? "" : String(v);
  });
}

function defaultSubject(ctx) {
  return `${ctx.broker || "Invoice"} — ${ctx.jobNumber || "Job"} — ${ctx.date || ""}`.trim();
}

function defaultBodyHtml(ctx) {
  // Plain, professional default. Brokers can override per-broker in
  // RateCards if they want something more specific.
  return `<p>Hi team,</p>
<p>Attached are the invoice workbook and POD package for
${ctx.broker || "this job"} — <strong>${ctx.jobNumber || "(no #)"}</strong>
on ${ctx.date || ""} (${ctx.jobType || ""}).</p>
<p>Total: <strong>${ctx.total || "—"}</strong></p>
<p>Thanks,<br/>ST Services</p>`;
}

// ── Public: compose draft for an approved job ────────────────────────

/**
 * Compose a Gmail draft for an approved job and return the Gmail draft ID.
 *
 *   job:       the job doc (post-approval; should have invoice fields set)
 *   broker:    the broker doc (used for invoiceEmail / templates)
 *   billing:   computeJobBilling result (used to fill {total})
 *   files:     array of { filename, mimeType, blob } — workbooks + POD PDF
 *
 * Returns { draftId, threadId } on success.
 * Throws if Gmail isn't connected, broker has no invoiceEmail, or the
 * API rejects the request. Callers should treat failure as non-fatal —
 * the invoice has already been created at this point; the owner can
 * still 📦 download and email manually.
 */
export async function composeJobDraft({ job, broker, billing, files }) {
  if (!broker?.invoiceEmail) {
    throw new Error(`Broker "${broker?.name || "?"}" has no invoiceEmail configured — skipping draft.`);
  }
  const accessToken = await getAccessToken({ interactive: true });

  const ctx = {
    broker: broker.name || job.brokerName || "",
    jobNumber: job.jobNumber || "",
    date: job.scheduledDate || "",
    total: billing?.total != null ? `$${Number(billing.total).toFixed(2)}` : "",
    jobType: job.jobType || "",
  };
  const subject = fillTemplate(broker.invoiceSubject, ctx) || defaultSubject(ctx);
  const body = broker.invoiceBody
    ? fillTemplate(broker.invoiceBody, ctx).replace(/\n/g, "<br/>")
    : defaultBodyHtml(ctx);

  const raw = await buildMimeMessage({
    to: broker.invoiceEmail,
    subject,
    bodyHtml: body,
    attachments: files,
  });

  // Resolve which Google account this token belongs to so the deep link
  // can target it explicitly. Non-blocking — if it fails we fall back
  // to the generic /u/0/ URL which may open the wrong account, but
  // that's still better than nothing.
  const authEmail = await fetchUserEmail(accessToken);

  const resp = await fetch("https://gmail.googleapis.com/gmail/v1/users/me/drafts", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message: { raw } }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Gmail API ${resp.status}: ${text.slice(0, 200)}`);
  }
  const data = await resp.json();
  return {
    draftId: data.id,
    threadId: data.message?.threadId || null,
    authEmail,
  };
}

/**
 * Deep-link URL into Gmail's compose view for a specific draft.
 *
 * The /u/0/ path opens whichever Google account the browser has at
 * index 0, which usually ISN'T the one the owner Connected. To open
 * the correct account we append ?authuser={email} when we know it —
 * Gmail honors that param and switches accounts automatically. Without
 * the param it falls back to /u/0/ behavior (and the user has to
 * switch accounts manually via the avatar dropdown).
 */
export function gmailDraftUrl(draftId, authEmail) {
  if (!draftId) return null;
  const base = `https://mail.google.com/mail/u/0/#drafts/${draftId}`;
  if (!authEmail) return base;
  // The hash has to come AFTER the query string for Gmail to parse
  // both correctly.
  return `https://mail.google.com/mail/?authuser=${encodeURIComponent(authEmail)}#drafts/${draftId}`;
}

// ── Per-user "is connected?" status persistence ──────────────────────
// Cached access tokens don't survive a reload, but we still want the
// Settings page to remember "yes, the owner has connected Gmail at
// least once" so we can show the right UI (and skip the silent re-auth
// attempt if they haven't). A boolean flag on the company doc is
// enough — the actual token is re-fetched on demand.

export async function setGmailLinkedFlag(companyId, linked) {
  if (!companyId) return;
  try {
    await updateCompany(companyId, { gmailLinked: !!linked });
  } catch (err) {
    console.warn("Couldn't persist gmailLinked flag", err);
  }
}

export async function getGmailLinkedFlag(companyId) {
  if (!companyId) return false;
  try {
    const c = await getCompany(companyId);
    return !!c?.gmailLinked;
  } catch {
    return false;
  }
}
